import React from "react";
import {
  Box,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text
} from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppButton from "@/features/line-sheet-sets/app-button";
import { lineSheetProductsMutationApi } from "@/shared/api/line-sheet-products/mutation";
import useIdentity from "@/features/ui/hooks/use-identity";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppSeasonSelect from "@/features/line-sheet-sets/app-season-select";
import { apiOrderApi } from "@/features/api-order/api";
import { StoredObject } from "@/features/types";
import AppImageFileController from "@/shared/components/app-image-file-controller";
import AppSelect from "@/features/line-sheet-sets/app-select";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  lineSheetSetId: number;
  lineSheetId: number;
  lineSheetTabIndex: number;
}

export default function AddLineSheetProductModal({ isOpen, onClose, onCancel, lineSheetSetId, lineSheetId, lineSheetTabIndex }: Props) {
  const { tTitle, tSentence } = useI18nHelper();
  const identity = useIdentity();

  const [openAdditionalInfo, setOpenAdditionalInfo] = React.useState<boolean>(false);

  const [sku, setSku] = React.useState<string>("");
  const [brandId, setBrandId] = React.useState<number | null>(null);
  const [brandName, setBrandName] = React.useState<string>("");
  // 가격은 입력 중간상태("49.")를 보존하기 위해 문자열로 관리하고, 숫자 변환은 제출 시점에만 한다.
  const [supplyPrice, setSupplyPrice] = React.useState<string>("");
  const [supplyPriceCurrency, setSupplyPriceCurrency] = React.useState<string>("EUR");
  const [costPrice, setCostPrice] = React.useState<string>("");
  const [costPriceCurrency, setCostPriceCurrency] = React.useState<string>("EUR");
  const [retailPrice, setRetailPrice] = React.useState<string>("");
  const [retailPriceCurrency, setRetailPriceCurrency] = React.useState<string>("EUR");
  const [madeIn, setMadeIn] = React.useState<string>("");
  const [category, setCategory] = React.useState<string>("");
  const [subCategory, setSubCategory] = React.useState<string>("");
  const [colorName, setColorName] = React.useState<string>("");
  const [gender, setGender] = React.useState<string>("");
  const [season, setSeason] = React.useState<string>("");
  const [material, setMaterial] = React.useState<string>("");
  const [note, setNote] = React.useState<string>("");

  const [ productImageFile, setProductImageFile ] = React.useState<Pick<StoredObject, "id" | "name" | "url"> | undefined>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  // 가격 문자열 → 숫자(빈값/형식오류는 0). 제출/검증에만 사용.
  const toNumber = (value: string) => {
    const n = parseFloat(value);
    return isNaN(n) ? 0 : n;
  };

  // 숫자와 소수점만 허용(중간상태 "49." 유지). 빈 문자열 허용.
  const handlePriceChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        setter(value);
      }
    };

  const handleClose = () => {
    setSku("");
    setBrandId(null);
    setBrandName("");
    setSupplyPrice("");
    setSupplyPriceCurrency("EUR");
    setCostPrice("");
    setCostPriceCurrency("EUR");
    setRetailPrice("");
    setRetailPriceCurrency("EUR");
    setMadeIn("");
    setCategory("");
    setSubCategory("");
    setColorName("");
    setGender("");
    setSeason("");
    setMaterial("");
    setNote("");
    onClose();
  };

  const handleCancel = () => {
    setSku("");
    setBrandId(null);
    setBrandName("");
    setSupplyPrice("");
    setSupplyPriceCurrency("EUR");
    setCostPrice("");
    setCostPriceCurrency("EUR");
    setRetailPrice("");
    setRetailPriceCurrency("EUR");
    setMadeIn("");
    setCategory("");
    setSubCategory("");
    setColorName("");
    setGender("");
    setSeason("");
    setMaterial("");
    setNote("");
    onCancel();
  }

  const isOrderable = sku !== "" && brandId !== null && (toNumber(supplyPrice) !== 0 || toNumber(costPrice) !== 0 || toNumber(retailPrice) !== 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius={"8px"} overflow={"hidden"}>
        <ModalHeader background={"#F7F9FA"} padding={"12px 16px"}>
          <Text textStyle={"body-md-bold"} color={"#444444"} verticalAlign={"center"}>
            {tTitle("add_product")}
          </Text>
        </ModalHeader>
        <ModalBody display="flex" flexDirection={"column"} gap={"12px"} >
          <Flex>
            <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Flex width={"124px"}>
                <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                  상품 사진
                </Text>
              </Flex>
              <Box width={"100%"}>
                <AppImageFileController
                  buttonText={"상품 이미지"}
                  isLoading={isLoading}
                  value={productImageFile}
                  onChange={(value) => {
                    setIsLoading(true);
                    if (value) {
                      apiOrderApi.uploadUnoFile(value).then((res) => {
                        if (res.data) {
                          setProductImageFile({
                            id: res.data.id,
                            name: res.data.name,
                            url: res.data.url
                          });
                        }
                      });
                    } else {
                      setProductImageFile(undefined);
                    }
                    setIsLoading(false);
                  }}
                />
              </Box>
            </Flex>
          </Flex>
          <Flex>
            <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Flex width={"124px"}>
                <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                  SKU
                </Text>
                <Text textStyle={"body-sm-regular"} color={"#CC1D1D"} paddingLeft={"4px"}>
                  *
                </Text>
              </Flex>
              <Input
                height={"28px"}
                placeholder={"예 : 8097588 A2021"}
                fontSize={"13px"}
                fontWeight={"400"}
                padding={"4px 8px"}
                value={sku}
                onChange={(e) => setSku(e.target.value)}
              />
            </Flex>
          </Flex>
          <Flex>
            <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Flex width={"124px"}>
                <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                  브랜드
                </Text>
                <Text textStyle={"body-sm-regular"} color={"#CC1D1D"} paddingLeft={"4px"}>
                  *
                </Text>
              </Flex>
              <AppCompanySelect
                key={"AgencyLineSheetSetListFilter_Brand"}
                name={'브랜드 선택'}
                types={["BRAND"]}
                id={brandId ?? undefined}
                width={"100%"}
                onSelect={(company) => {
                  if (company) {
                    setBrandId(company.id);
                    setBrandName(company.name);
                  }
                }}
                isClearable={false}
              />
            </Flex>
          </Flex>
          <Flex>
            <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Flex width={"124px"}>
                <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                  원본 브랜드
                </Text>
              </Flex>
              <Input
                height={"28px"}
                placeholder={"공급사 시트에 표기된 브랜드명"}
                fontSize={"13px"}
                fontWeight={"400"}
                padding={"4px 8px"}
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
            </Flex>
          </Flex>
          <Flex>
            <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Flex width={"124px"}>
                <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                  판매가
                </Text>
              </Flex>
              <Flex gap={"8px"} width={"100%"}>
                <AppSelect<string>
                  width={"80px"}
                  borderWidth={"1px"}
                  isMultiple={false}
                  isClearable={false}
                  name={tTitle("common\:currency")}
                  value={supplyPriceCurrency}
                  options={[
                    { name: "EUR", value: "EUR" },
                    { name: "USD", value: "USD" },
                    { name: "HKD", value: "HKD" },
                    { name: "KRW", value: "KRW" },
                  ] as AppSelectOption<string>[]}
                  onSelect={(value) => {
                    setSupplyPriceCurrency(value || "EUR");
                  }}
                />
                <Input
                  height={"28px"}
                  placeholder={"판매가"}
                  fontSize={"13px"}
                  fontWeight={"400"}
                  padding={"4px 8px"}
                  value={supplyPrice}
                  onChange={handlePriceChange(setSupplyPrice)}
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex>
            <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Flex width={"124px"}>
                <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                  도매가
                </Text>
              </Flex>
              <Flex gap={"8px"} width={"100%"}>
                <AppSelect<string>
                  width={"80px"}
                  borderWidth={"1px"}
                  isMultiple={false}
                  isClearable={false}
                  name={tTitle("common\:currency")}
                  value={costPriceCurrency}
                  options={[
                    { name: "EUR", value: "EUR" },
                    { name: "USD", value: "USD" },
                    { name: "HKD", value: "HKD" },
                    { name: "KRW", value: "KRW" },
                  ] as AppSelectOption<string>[]}
                  onSelect={(value) => {
                    setCostPriceCurrency(value || "EUR");
                  }}
                />
                <Input
                  height={"28px"}
                  placeholder={"도매가"}
                  fontSize={"13px"}
                  fontWeight={"400"}
                  padding={"4px 8px"}
                  value={costPrice}
                  onChange={handlePriceChange(setCostPrice)}
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex>
            <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Flex width={"124px"}>
                <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                  소매가
                </Text>
              </Flex>
              <Flex gap={"8px"} width={"100%"}>
                <AppSelect<string>
                  width={"80px"}
                  borderWidth={"1px"}
                  isMultiple={false}
                  isClearable={false}
                  name={tTitle("sort_by")}
                  value={retailPriceCurrency}
                  options={[
                    { name: "EUR", value: "EUR" },
                    { name: "USD", value: "USD" },
                    { name: "HKD", value: "HKD" },
                    { name: "KRW", value: "KRW" },
                  ] as AppSelectOption<string>[]}
                  onSelect={(value) => {
                    setRetailPriceCurrency(value || "EUR");
                  }}
                />
                <Input
                  height={"28px"}
                  placeholder={"소매가"}
                  fontSize={"13px"}
                  fontWeight={"400"}
                  padding={"4px 8px"}
                  value={retailPrice}
                  onChange={handlePriceChange(setRetailPrice)}
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex>
            <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
              <Flex width={"124px"}>
                <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                  원산지
                </Text>
              </Flex>
              <Input
                height={"28px"}
                placeholder={"예 : Italy, Spain"}
                fontSize={"13px"}
                fontWeight={"400"}
                padding={"4px 8px"}
                value={madeIn}
                onChange={(e) => setMadeIn(e.target.value)}
              />
            </Flex>
          </Flex>
          <Flex justifyContent={"flex-end"}>
            <Flex
              onClick={() => setOpenAdditionalInfo(!openAdditionalInfo)} cursor={"pointer"} alignItems={"center"}
            >
              <Text textStyle={"caption-bold"}>
                추가 정보 펼치기
              </Text>
              <Box>
                {openAdditionalInfo ? (
                  <SVG src={"/icons-v1_1/chevron-up.svg"} width={"16px"} height={"16px"} />
                ) : (
                  <SVG src={"/icons-v1_1/chevron-down.svg"} width={"16px"} height={"16px"} />
                )}
              </Box>
            </Flex>
          </Flex>
          {
            openAdditionalInfo &&
            <>
              <Flex>
                <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                  <Flex width={"124px"}>
                    <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                      카테고리
                    </Text>
                  </Flex>
                  <Input
                    height={"28px"}
                    placeholder={"예 : Bag, Accessories"}
                    fontSize={"13px"}
                    fontWeight={"400"}
                    padding={"4px 8px"}
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                </Flex>
              </Flex>
              <Flex>
                <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                  <Flex width={"124px"}>
                    <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                      하위 카테고리
                    </Text>
                  </Flex>
                  <Input
                    height={"28px"}
                    placeholder={"예 : Top, Pants"}
                    fontSize={"13px"}
                    fontWeight={"400"}
                    padding={"4px 8px"}
                    value={subCategory}
                    onChange={(e) => setSubCategory(e.target.value)}
                  />
                </Flex>
              </Flex>
              <Flex>
                <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                  <Flex width={"124px"}>
                    <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                      색상
                    </Text>
                  </Flex>
                  <Input
                    height={"28px"}
                    placeholder={"예 : Black, White"}
                    fontSize={"13px"}
                    fontWeight={"400"}
                    padding={"4px 8px"}
                    value={colorName}
                    onChange={(e) => setColorName(e.target.value)}
                  />
                </Flex>
              </Flex>
              <Flex>
                <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                  <Flex width={"124px"}>
                    <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                      성별
                    </Text>
                  </Flex>
                  <Input
                    height={"28px"}
                    placeholder={"예 : Man, Woman"}
                    fontSize={"13px"}
                    fontWeight={"400"}
                    padding={"4px 8px"}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  />
                </Flex>
              </Flex>
              <Flex>
                <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                  <Flex width={"124px"}>
                    <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                      시즌
                    </Text>
                  </Flex>
                  <AppSeasonSelect
                    width={"100%"}
                    value={season}
                    onSelect={(value) => {
                      if (value) {
                        setSeason(value);
                      }
                    }}
                  />
                </Flex>
              </Flex>
              <Flex>
                <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                  <Flex width={"124px"}>
                    <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                      소재
                    </Text>
                  </Flex>
                  <Input
                    height={"28px"}
                    placeholder={"예 : Polyester, Polyurethane"}
                    fontSize={"13px"}
                    fontWeight={"400"}
                    padding={"4px 8px"}
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                  />
                </Flex>
              </Flex>
              <Flex>
                <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                  <Flex width={"124px"}>
                    <Text color={"#6F6F6C"} textStyle={"body-sm-regular"}>
                      기타 정보
                    </Text>
                  </Flex>
                  <Input
                    height={"28px"}
                    placeholder={"기타 정보"}
                    fontSize={"13px"}
                    fontWeight={"400"}
                    padding={"4px 8px"}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Flex>
              </Flex>
            </>
          }
        </ModalBody>
        <ModalFooter display={"flex"} justifyContent={"end"} gap={"12px"}>
          <AppButton variant={"normal"} onClick={handleCancel}>
            <Text textStyle={"body-sm-bold"} color={"#444444"}>
              {tTitle("cancel")}
            </Text>
          </AppButton>
          <AppButton
            variant={"primary"}
            isDisabled={!isOrderable}
            onClick={async () => {
              if (!isOrderable) {
                return;
              }
              const supplyPriceNumber = toNumber(supplyPrice);
              const costPriceNumber = toNumber(costPrice);
              const retailPriceNumber = toNumber(retailPrice);
              const productData = {
                sku,
                brand_id: brandId,
                brand_name: brandName,
                supply_price: supplyPriceNumber === 0 ? null : supplyPriceNumber,
                supply_price_currency: supplyPriceCurrency,
                cost_price: costPriceNumber === 0 ? null : costPriceNumber,
                cost_price_currency: costPriceCurrency,
                retail_price: retailPriceNumber === 0 ? null : retailPriceNumber,
                retail_price_currency: retailPriceCurrency,
                made_in: madeIn,
                category,
                sub_category: subCategory,
                color_name: colorName,
                gender: gender,
                season: season,
                material: material,
                note: note,
                product_image_file_id: productImageFile?.id ?? null,
              }

              const res = await lineSheetProductsMutationApi.createLineSheetProduct(
                identity?.company?.id!,
                lineSheetSetId,
                lineSheetId,
                lineSheetTabIndex,
                productData
              );

              handleClose();
            }}
          >
            <Text textStyle={"body-sm-bold"} color={"#FFFFFF"}>
              {tTitle("add")}
            </Text>
          </AppButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
